import React, { useCallback } from 'react'

import { RecipeAlternativeOptions } from './RecipeAlternativeOptions'
import { DeviceType, useDeviceType } from '@library/dom'
import { AlternativeOptions } from '../../model/alternativeOptions/alternativeOptions'
import { queueTrackingEvent } from '@library/tracking-integration'
import {
  CollectionUUID,
  MenuApiPreferencesResponse,
  Recipe,
  RecipeCoreId,
  RecipeUUID,
} from '@library/menu-service'
import {
  DesktopCollapsable,
  MobileCollapsable,
} from './SwapOptionsCollapsable/SwapOptionsCollapsable'
import { IngredientAlternativeOptions } from './IngredientAlternativeOptions'
import { Box, DisplayPosition, ZIndex } from '@gousto-internal/citrus-react'
import { Education } from './Education'

export type TrackRecipeAlternativeOptionsMenuOpen = {
  action: 'recipe-alternative-options-menu-open'
  recipeId: string
  collectionId: string
}

export type TrackRecipeAlternativeOptionsMenuSwapRecipes = {
  action: 'recipe-alternative-options-menu-swap-recipes'
  previousRecipeId: string
  nextRecipeId: string
  collectionId: string
}

export type TrackClickCustomise = {
  action: 'click_customise'
  properties: {
    collection_id: string
    recipe_id: string
    recipe_uuid: string
    menu_uuid: string | null
    event_screen: string
    recipe_list_position?: number
  }
}

type SwapAlternativeOptionsProps = {
  currentRecipeCoreId: RecipeCoreId
  currentCollectionId: CollectionUUID
  menuId: string | null
  menuPreferences: MenuApiPreferencesResponse | undefined
  recipe: Recipe
  originalId: RecipeCoreId
  alternativeOptions: AlternativeOptions
  isLoading: boolean
  reachedLimit: boolean
  addRecipeToBasket: (id: RecipeCoreId) => void
  selectVariant: ({
    variant,
    variantOutOfStock,
  }: {
    variant: Recipe
    variantOutOfStock: boolean
  }) => void
  onOpenAlternativeOptions?: () => void
  canAddRecipes: boolean
  removeRecipe: (id: RecipeCoreId) => void
  quantity: number
  recipeIndex?: number
  showCustomisationEducation?: boolean
  closeCustomisationEducation?: () => void
}

export function SwapAlternativeOptions({
  currentRecipeCoreId,
  currentCollectionId,
  menuId,
  menuPreferences,
  recipe,
  originalId,
  alternativeOptions,
  isLoading,
  reachedLimit,
  selectVariant,
  addRecipeToBasket,
  onOpenAlternativeOptions,
  canAddRecipes,
  removeRecipe,
  quantity,
  recipeIndex,
  showCustomisationEducation,
  closeCustomisationEducation,
}: SwapAlternativeOptionsProps) {
  const deviceType = useDeviceType()
  const hasReadyToHeatBanner = recipe.preparationType === 'heat-only'
  const bottomOffset = hasReadyToHeatBanner ? '2rem' : '0.5rem'

  const shouldShowEducation = showCustomisationEducation && closeCustomisationEducation

  const onOpen = useCallback(() => {
    queueTrackingEvent<TrackRecipeAlternativeOptionsMenuOpen>({
      action: 'recipe-alternative-options-menu-open',
      recipeId: currentRecipeCoreId,
      collectionId: currentCollectionId,
    })

    if (!isLoading && alternativeOptions.optionsType === 'ingredients') {
      queueTrackingEvent<TrackClickCustomise>({
        action: 'click_customise',
        properties: {
          collection_id: currentCollectionId,
          recipe_id: currentRecipeCoreId,
          menu_uuid: menuId,
          recipe_uuid: recipe.id,
          event_screen: 'view_recipe_list_screen',
          recipe_list_position: recipeIndex,
        },
      })
    }

    if (onOpenAlternativeOptions) {
      onOpenAlternativeOptions()

      if (alternativeOptions.optionsType === 'ingredients') {
        closeCustomisationEducation?.()
      }
    }
  }, [
    currentRecipeCoreId,
    currentCollectionId,
    isLoading,
    alternativeOptions.optionsType,
    onOpenAlternativeOptions,
    menuId,
    recipe.id,
    recipeIndex,
    closeCustomisationEducation,
  ])

  const alternativeOptionsStrategy = useCallback(
    ({ close, isMobile }: { close: () => void; isMobile: boolean }) => {
      if (alternativeOptions.optionsType === 'none') {
        return null
      }

      if (alternativeOptions.optionsType === 'recipes') {
        return (
          <RecipeAlternativeOptions
            currentRecipeCoreId={currentRecipeCoreId}
            onChangeCheckedRecipe={({ previousRecipeId, nextRecipeId }) => {
              queueTrackingEvent<TrackRecipeAlternativeOptionsMenuSwapRecipes>({
                action: 'recipe-alternative-options-menu-swap-recipes',
                previousRecipeId,
                nextRecipeId,
                collectionId: currentCollectionId,
              })
              close()
            }}
            selectVariant={selectVariant}
            options={alternativeOptions.options}
            isMobile={isMobile}
            isLoading={isLoading}
          />
        )
      }
      if (alternativeOptions.optionsType === 'ingredients') {
        return (
          <IngredientAlternativeOptions
            menuId={menuId}
            currentCollectionId={currentCollectionId}
            baseRecipeId={originalId}
            recipeUuid={recipe.id}
            alternativeOptions={alternativeOptions}
            isLoading={isLoading}
            reachedLimit={reachedLimit}
            addRecipeToBasket={(id: RecipeUUID) => {
              const coreId = alternativeOptions?.options?.find(
                (option) => option.recipe.id === id,
              )?.recipeId

              if (coreId) {
                addRecipeToBasket(coreId)
              }

              if (quantity === 0) {
                close()
              }
            }}
            onSelectRecipe={(id: RecipeUUID) => {
              const selectedOption = alternativeOptions?.options?.find(
                (option) => option.recipe.id === id,
              )
              const variant = selectedOption?.recipe as Recipe

              selectVariant({
                variant,
                variantOutOfStock: Boolean(selectedOption?.isOutOfStock),
              })
            }}
            canAddRecipes={canAddRecipes}
            removeRecipe={() => removeRecipe(currentRecipeCoreId)}
            quantity={quantity}
          />
        )
      }
    },
    [
      alternativeOptions,
      currentRecipeCoreId,
      selectVariant,
      isLoading,
      currentCollectionId,
      menuId,
      originalId,
      recipe.id,
      reachedLimit,
      canAddRecipes,
      quantity,
      addRecipeToBasket,
      removeRecipe,
    ],
  )

  if (alternativeOptions.optionsType === 'none') {
    return null
  }

  const component =
    deviceType === DeviceType.MOBILE ? (
      <MobileCollapsable
        onOpen={onOpen}
        optionsType={alternativeOptions.optionsType}
        currentCollectionId={currentCollectionId}
        recipe={recipe}
        menuPreferences={menuPreferences}
        bottomOffset={bottomOffset}
      >
        {alternativeOptionsStrategy}
      </MobileCollapsable>
    ) : (
      <DesktopCollapsable
        optionsType={alternativeOptions.optionsType}
        onOpen={onOpen}
        bottomOffset={bottomOffset}
      >
        {alternativeOptionsStrategy}
      </DesktopCollapsable>
    )

  return (
    <>
      {component}
      {shouldShowEducation && (
        <Box
          position={DisplayPosition.Absolute}
          top="100%"
          left="50%"
          zIndex={ZIndex.Mask}
          style={{ transform: 'translateX(-50%)', marginTop: '-2px' }}
          width="100%"
        >
          <Education onClose={closeCustomisationEducation} />
        </Box>
      )}
    </>
  )
}
